import {
    API_ENDPOINTS,
    customToast,
    DateFormats,
    formatDateFns,
    route,
    SelectOption,
    Stepper,
    StringHelpers,
    SummaryLineItems,
    useDisabledContext,
    useLangContext,
    useQueries,
    Variants,
} from 'carrier-fe';
import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { ClaimsFormData, DistributorDetails } from '../../types/claim';
import { UseFormReturn } from '../../types/journey';
import { DISTRIBUTOR_CLAIM_TYPES, QUERY_KEYS } from '../../util/Constants';
import { fetchProductLines, fetchProductModels } from '../../util/toshiba-utils';

function ClaimsStep4({
    form,
}: {
    form: UseFormReturn<ClaimsFormData, any>;
}) {
    // Hooks and Contexts
    const { store } = form;
    const { crud, fields } = useLangContext();
    const { setDisabled } = useDisabledContext();

    // State Management
    const [productLines, setProductLines] = useState<SelectOption[]>([]);
    const [productModels, setProductModels] = useState<SelectOption[]>([]);
    const [installationSystems, setInstallationSystems] = useState<SelectOption[]>([]);
    const [distributorDetails, setDistributorDetails] = useState<DistributorDetails>({
        distributor_claim_type: null,
        dispatch_note_field_label: null,
        dispatch_note_required: false,
    });
    const [isFetching, setIsFetching] = useState(false);

    // Queries
    const getQueries = () => {
        return [
            {
                key: QUERY_KEYS.INSTALLER_COMPANIES,
                config: {
                    method: 'GET',
                    url: route(`${API_ENDPOINTS.GENERIC.SELECT.COMPANIES}?types[]=installer`),
                },
            },
            ...store?.company_id ? [
                {
                    key: QUERY_KEYS.CUSTOMERS,
                    config: {
                        method: 'GET',
                        url: route(API_ENDPOINTS.GENERIC.SELECT.CUSTOMERS, {
                            company_id: store.company_id,
                        }),
                    },
                },
                {
                    key: QUERY_KEYS.DISTRIBUTORS,
                    config: {
                        method: 'GET',
                        url: route(API_ENDPOINTS.GENERIC.SELECT.DISTRIBUTORS, {
                            companyId: store.company_id,
                        }),
                    },
                },
                {
                    key: QUERY_KEYS.INSTALLATIONS,
                    config: {
                        method: 'GET',
                        url: route(API_ENDPOINTS.GENERIC.SELECT.INSTALLATIONS, {
                            companyId: store.company_id,
                        }),
                    },
                },
            ] : [],
            {
                key: QUERY_KEYS.PRODUCT_CHILDREN,
                config: {
                    method: 'GET',
                    url: route(API_ENDPOINTS.GENERIC.SELECT.PRODUCT.CHILDREN),
                },
            },
            ...store?.product_child_id ? [
                {
                    key: QUERY_KEYS.PRODUCT_TYPES,
                    config: {
                        method: 'GET',
                        url: route(API_ENDPOINTS.GENERIC.SELECT.PRODUCT.TYPES, {
                            product_child_id: store.product_child_id,
                        }),
                    },
                },
            ] : [],
        ];
    };

    const {
        data: selectOptions,
        error,
        isLoading,
    } = useQueries<{ data: SelectOption[] }>(getQueries());

    // Effects

    // Fetch product lines, product models, installation systems, and distributor details
    useEffect(() => {
        const fetchAdditionalData = async () => {
            setIsFetching(true);
            setDisabled(true);

            try {
                const promises: Promise<void>[] = [];

                // Fetch product lines
                if (store?.product_child_id && store?.product_type_id) {
                    promises.push(
                        fetchProductLines({
                            childId: store.product_child_id,
                            typeId: store.product_type_id,
                        }).then((lines) => {
                            setProductLines(lines || []);
                        })
                    );
                }

                // Fetch product models
                if (store?.product_child_id && store?.product_type_id && store?.product_line_id) {
                    promises.push(
                        fetchProductModels({
                            childId: store.product_child_id,
                            typeId: store.product_type_id,
                            lineId: store.product_line_id,
                        }).then((models) => {
                            setProductModels(models || []);
                        })
                    );
                }

                // Fetch installation systems
                if (store?.toshiba_installation_id) {
                    promises.push(
                        axios.get<{ data: SelectOption[] }>(
                            route(API_ENDPOINTS.GENERIC.SELECT.INSTALLATION_SYSTEMS, {
                                installationId: store.toshiba_installation_id,
                            })
                        ).then((response) => {
                            setInstallationSystems(response.data?.data ?? []);
                        })
                    );
                }

                // Fetch distributor details
                if (store?.distributor_id) {
                    promises.push(
                        axios.get<{ data: DistributorDetails }>(
                            route(API_ENDPOINTS.REGISTRATION.CLAIM.DISTRIBUTOR_CONFIG,
                                { distributorId: store.distributor_id }
                            )
                        ).then((response) => {
                            const distributor = response.data?.data;

                            if (distributor) {
                                setDistributorDetails({
                                    distributor_claim_type: distributor.distributor_claim_type ?? null,
                                    dispatch_note_field_label: distributor.dispatch_note_field_label ?? null,
                                    dispatch_note_required: distributor.dispatch_note_required ?? false,
                                });
                            }
                        })
                    );
                }

                await Promise.all(promises);
            } catch (err: any) {
                customToast({
                    title: err?.response?.data?.message || 'An error occurred.',
                    variant: Variants.Danger,
                });
            } finally {
                setIsFetching(false);
                setDisabled(false);
            }
        };

        fetchAdditionalData();
    }, []);

    // Handle query errors
    useEffect(() => {
        if (!error) return;

        Object.values(error).forEach((err: any) => {
            if (!err) return;

            customToast({
                title: err?.response?.data?.message || 'An error occurred.',
                variant: Variants.Danger,
            });
        });
    }, [error]);

    // Derived States
    const isPostRepair = useMemo(
        () => distributorDetails.distributor_claim_type === DISTRIBUTOR_CLAIM_TYPES.POST_REPAIR,
        [distributorDetails.distributor_claim_type]
    );

    const isPreRepair = useMemo(
        () => distributorDetails.distributor_claim_type === DISTRIBUTOR_CLAIM_TYPES.PRE_REPAIR,
        [distributorDetails.distributor_claim_type]
    );

    const findOption = (value: string, options: SelectOption[]): string => {
        try {
            return options?.find((option) => option.value === value)?.label || '-';
        } catch {
            return '-';
        }
    };

    const summaryConf = useMemo(() => {
        if (!selectOptions) return [];

        return summaryConfig({
            store,
            selectOptions,
            productLines,
            productModels,
            installationSystems,
            isPostRepair,
            isPreRepair,
            findOption,
            fields,
            crud,
        });
    }, [
        store,
        selectOptions,
        productLines,
        productModels,
        installationSystems,
        isPostRepair,
        isPreRepair,
        fields,
        crud,
    ]);

    return (
        <Stepper.Step
            loading={isLoading || isFetching}
            title={crud?.sub_titles?.summary || 'Summary'}
        >
            <div className="d-flex flex-column gap-3">
                {summaryConf?.map((item, index) => (
                    <SummaryLineItems
                        maxPerView={3}
                        key={index}
                        title={item.title}
                        items={item.items}
                    />
                ))}
            </div>
        </Stepper.Step>
    );
}

export default ClaimsStep4;

// =============================================================================
// SUMMARY CONFIGURATION
// =============================================================================

type SummaryItems = React.ComponentProps<typeof SummaryLineItems>['items'];

interface SummaryConfigParams {
    store: ClaimsFormData;
    selectOptions: Record<string, { data: SelectOption[] } | null>;
    productLines: SelectOption[];
    productModels: SelectOption[];
    installationSystems: SelectOption[];
    isPostRepair: boolean;
    isPreRepair: boolean;
    findOption: (value: string, options: SelectOption[]) => string;
    fields: any;
    crud: any;
}

const summaryConfig = ({
    store,
    selectOptions,
    productLines,
    productModels,
    installationSystems,
    isPostRepair,
    isPreRepair,
    findOption,
    fields,
    crud,
}: SummaryConfigParams): { title: string; items: SummaryItems }[] => {
    const companies = selectOptions[QUERY_KEYS.INSTALLER_COMPANIES]?.data ?? [];
    const customers = selectOptions[QUERY_KEYS.CUSTOMERS]?.data ?? [];
    const distributors = selectOptions[QUERY_KEYS.DISTRIBUTORS]?.data ?? [];
    const installations = selectOptions[QUERY_KEYS.INSTALLATIONS]?.data ?? [];
    const productChildren = selectOptions[QUERY_KEYS.PRODUCT_CHILDREN]?.data ?? [];
    const productTypes = selectOptions[QUERY_KEYS.PRODUCT_TYPES]?.data ?? [];

    const yesNoMap: Record<string, string> = {};

    if (crud?.options?.boolean) {
        crud.options.boolean.forEach((option: SelectOption) => {
            yesNoMap[String(option.value)] = option.label;
        });
    }

    const formatYesNo = (value: boolean | undefined): string => {
        if (value === undefined || value === null) return '-';
        return yesNoMap[value ? '1' : '0'] || (value ? 'Yes' : 'No');
    };

    return [
        // Step 1: Company, Customer & Distributor Details
        {
            title: `${crud?.sub_titles?.step || 'Step'} 1: ${
                crud?.sub_titles?.company || 'Company'
            }, ${crud?.sub_titles?.customer || 'Customer'} & ${
                crud?.sub_titles?.distributor || 'Distributor'
            } ${crud?.sub_titles?.details || 'Details'}`,
            items: [
                {
                    label: StringHelpers.title(
                        fields?.company_id
                            || fallbackFieldLabels.company_id
                    ),
                    value: findOption(store?.company_id, companies),
                },
                {
                    label: StringHelpers.title(
                        fields?.account_number
                            || fallbackFieldLabels.account_number
                    ),
                    value: store?.account_number || '-',
                },
                ...(store?.toshiba_installation_id ? [
                    {
                        label: StringHelpers.title(
                            `${crud?.models?.installation || 'Installation'}`
                        ),
                        value: findOption(store.toshiba_installation_id, installations),
                    },
                ] : []),
                ...(store?.installation_date ? [
                    {
                        label: StringHelpers.title(
                            fields?.installation_date
                                || fallbackFieldLabels.installation_date
                        ),
                        value: formatDateFns(store.installation_date, DateFormats.DATE),
                    },
                ] : []),
                {
                    label: StringHelpers.title(
                        crud?.sub_titles?.customer
                            || fallbackFieldLabels.customer_id
                    ),
                    value: findOption(store?.customer_id, customers),
                },
                {
                    label: StringHelpers.title(
                        crud?.sub_titles?.distributor
                            || fallbackFieldLabels.distributor_id
                    ),
                    value: findOption(store?.distributor_id, distributors),
                },
            ],
        },
        // Step 2: System Specific Details
        {
            title: `${crud?.sub_titles?.step || 'Step'} 2: ${
                crud?.sub_titles?.system || 'System'
            } ${crud?.sub_titles?.details || 'Details'}`,
            items: [
                ...(store?.toshiba_installation_system_id ? [
                    {
                        label: StringHelpers.title(
                            `${crud?.models?.installation || 'Installation'} ${crud?.sub_titles?.system || 'System'}`
                        ),
                        value: findOption(store.toshiba_installation_system_id, installationSystems),
                    },
                ] : []),
                {
                    label: StringHelpers.title(
                        crud?.sub_titles?.system_type
                            || fallbackFieldLabels.product_child_id
                    ),
                    value: findOption(store?.product_child_id, productChildren),
                },
                {
                    label: StringHelpers.title(
                        crud?.sub_titles?.location
                            || fallbackFieldLabels.product_type_id
                    ),
                    value: findOption(store?.product_type_id, productTypes),
                },
                {
                    label: StringHelpers.title(
                        crud?.models?.product_line
                            || fallbackFieldLabels.product_line_id
                    ),
                    value: findOption(store?.product_line_id, productLines),
                },
                {
                    label: StringHelpers.title(
                        crud?.sub_titles?.model_number
                            || fallbackFieldLabels.product_id
                    ),
                    value: findOption(store?.product_id, productModels),
                },
                {
                    label: StringHelpers.title(
                        fields?.serial_number
                            || fallbackFieldLabels.serial_number
                    ),
                    value: store?.serial_number || '-',
                },
                {
                    label: StringHelpers.title(
                        fields?.dispatch_note_number
                            || fallbackFieldLabels.dispatch_note_number
                    ),
                    value: store?.dispatch_note_number || '-',
                },
                {
                    label: StringHelpers.title(
                        fields?.date_of_failure
                            || fallbackFieldLabels.date_of_failure
                    ),
                    value: formatDateFns(store?.date_of_failure, DateFormats.DATE),
                },
                ...(isPostRepair ? [
                    {
                        label: StringHelpers.title(
                            fields?.date_of_repair
                                || fallbackFieldLabels.date_of_repair
                        ),
                        value: formatDateFns(store?.date_of_repair, DateFormats.DATE),
                    },
                ] : []),
                ...(isPreRepair ? [
                    {
                        label: StringHelpers.title(
                            fields?.proposed_date_of_repair
                                || fallbackFieldLabels.proposed_date_of_repair
                        ),
                        value: formatDateFns(store?.proposed_date_of_repair, DateFormats.DATE),
                    },
                ] : []),
                {
                    label: StringHelpers.title(
                        fields?.quantity_of_parts
                            || fallbackFieldLabels.quantity_of_parts
                    ),
                    value: store?.quantity_of_parts || '-',
                },
            ],
        },
        // Step 3: Part Specific Details
        {
            title: `${crud?.sub_titles?.step || 'Step'} 3: ${
                crud?.models?.part || 'Part'
            } ${crud?.sub_titles?.details || 'Details'}`,
            items: [
                ...(store?.parts?.map((part, index) => ({
                    label: `${crud?.models?.part || 'Part'} ${index + 1}`,
                    value: [
                        {
                            label: StringHelpers.title(
                                fields?.part_number
                                    || fallbackFieldLabels.part_number
                            ),
                            value: part.part_number || '-',
                        },
                        {
                            label: StringHelpers.title(
                                fields?.description
                                    || fallbackFieldLabels.description
                            ),
                            value: part.description || '-',
                        },
                        ...(isPostRepair ? [
                            {
                                label: StringHelpers.title(
                                    fields?.replacement_dispatch_note_number
                                        || fallbackFieldLabels.replacement_dispatch_note_number
                                ),
                                value: part.replacement_dispatch_note_number || '-',
                            },
                        ] : []),
                        {
                            label: StringHelpers.title(
                                fields?.fault_symptoms
                                    || fallbackFieldLabels.fault_symptoms
                            ),
                            value: part.fault_symptoms || '-',
                        },
                        {
                            label: StringHelpers.title(
                                fields?.repair_details
                                    || fallbackFieldLabels.repair_details
                            ),
                            value: part.repair_details || '-',
                        },
                        {
                            label: StringHelpers.title(
                                fields?.previously_repaired
                                    || fallbackFieldLabels.previously_repaired
                            ),
                            value: formatYesNo(part.previously_repaired),
                        },
                    ],
                })) || []),
            ],
        },
    ];
};

// =============================================================================
// FALLBACK FIELD LABELS
// =============================================================================

const fallbackFieldLabels: Record<string, string> = {
    company_id: 'company',
    account_number: 'account number',
    installation_date: 'installation date',
    customer_id: 'customer',
    distributor_id: 'distributor',
    product_child_id: 'system type',
    product_type_id: 'location',
    product_line_id: 'product line',
    product_id: 'model number',
    serial_number: 'serial number',
    dispatch_note_number: 'despatch note number',
    date_of_failure: 'date of failure',
    date_of_repair: 'date of repair',
    proposed_date_of_repair: 'proposed date of repair',
    quantity_of_parts: 'quantity of parts',
    part_number: 'part number',
    description: 'description',
    replacement_dispatch_note_number: 'replacement despatch note number',
    fault_symptoms: 'fault symptoms',
    repair_details: 'repair details',
    previously_repaired: 'previously repaired',
};
